import React, { FC, ChangeEvent, ReactElement, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import SettingsCard from 'components/@common/Cards/SettingsCard';
import {
  GssSettingCharacterOrderData,
  GssCharacterOrder,
  Classroom
} from 'types/schema-generated-types';
import GET_GSS_CLASSROOM_SETTINGS from 'graph/settings/gss/get-gss-classroom-settings-query';
import UPDATE_GSS_CLASSROOM_CHARACTER_ORDER_SETTING from 'graph/settings/gss/update-gss-classroom-character-order-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface LetterAndNumberInputs {
  label: string;
  value: string;
}

interface LetterNumberOrderProps {
  classroom: Classroom;
  setting: GssSettingCharacterOrderData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
  panel: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const letterOptions: LetterAndNumberInputs[] = [
  {
    label: 'GSS Order (Letters in GSS order and numbers in numerical order)',
    value: GssCharacterOrder.gss
  },
  {
    label:
      'Alphabetical/Numerical Order (Letters in alphabetical order and numbers in numerical order)',
    value: GssCharacterOrder.natural
  },
  { label: 'Free Play (All letters and numbers unlocked)', value: GssCharacterOrder.freePlay }
];

const getClassroomGssSettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_GSS_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateGssClassroomSettingCharacterOrder }
      ): { gssClassroomSettings: any } => {
        const [{ value }] = updateGssClassroomSettingCharacterOrder;
        return {
          gssClassroomSettings: {
            ...cachedData?.gssClassroomSettings,
            characterOrder: value
          }
        };
      }
    }
  ])
});

const LetterNumberOrder: FC<LetterNumberOrderProps> = ({
  classroom,
  expanded,
  setting: settingValue,
  handleChangeExpanded,
  panel
}) => {
  const { t } = useTranslation('LetterNumberOrderSetting');
  const classes = useStyles();

  const { id: classroomId } = classroom;
  const [, { openAlert }] = useStore();
  const [updateGssClassroomSettingCharacterOrder] = useMutation(
    UPDATE_GSS_CLASSROOM_CHARACTER_ORDER_SETTING,
    getClassroomGssSettingsMutationCacheUpdate(+classroomId)
  );

  const [value, setValue] = useState(settingValue?.letters || GssCharacterOrder.gss);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value as GssCharacterOrder);
  };

  const handleOnCancel = (): void => setValue(settingValue?.letters || GssCharacterOrder.gss);

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateGssClassroomSettingCharacterOrder({
        variables: {
          data: {
            classroomId,
            value: {
              letters: value,
              numbers: value,
              wdtCapitals: value,
              wdtLowercase: value,
              wdtNumbers: value
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Letter and Number Order', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Letter and Number Order')}
      panel={panel}
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t(
        'Choose the letter and number order students will use (Default is GSS order).'
      )}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <RadioGroup
          aria-label="letter number setting"
          name="letterNumberSetting"
          value={value}
          onChange={handleChange}
        >
          {letterOptions.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`letter-number-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default LetterNumberOrder;
